import { Component, OnInit, OnDestroy } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ResellerService } from "./reseller.service";
import { ResellerTableRow, ResellerWithLicenses } from "./reseller.interface";
import { ToastrService } from "../shared/toastr.service";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { AddResellerClientDialogComponent } from "./add-reseller-client-dialog/add-reseller-client-dialog.component";
import { AttachDomainDialogComponent } from "./attach-domain-dialog/attach-domain-dialog.component";
import { Router } from "@angular/router";
import { StoreExtendedService } from "../shared/store-extended.service";
import {
  ChangeAccountAction,
  CHANGE_ACCOUNT_SUCCESS,
} from "../accounts/accounts.actions";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";

@Component({
  selector: "app-reseller",
  templateUrl: "./reseller.component.html",
  styleUrls: ["./reseller.component.scss"],
  standalone: false,
})
export class ResellerComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ["expand", "name", "licenseCount", "actions"];
  dataSource = new MatTableDataSource<ResellerTableRow>([]);
  loading = true;
  private destroy$ = new Subject<void>();
  private expandedDomains = new Set<string>();
  public partners = new Map<string, ResellerWithLicenses>();

  get mainDomainsCount(): number {
    return this.dataSource.data.filter((row) => !row.parentDomain).length;
  }

  constructor(
    private resellerService: ResellerService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private store: StoreExtendedService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.loading = true;
    this.resellerService
      .getPartnersWithLicenses()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.partners.clear();
          data.forEach((partner) => this.partners.set(partner.domain, partner));
          this.updateDataSource();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  toggleExpand(domain: string): void {
    if (this.expandedDomains.has(domain)) {
      this.expandedDomains.delete(domain);
    } else {
      this.expandedDomains.add(domain);
    }
    this.updateDataSource();
  }

  addClient(reseller: ResellerWithLicenses) {
    const dialogRef = this.dialog.open(AddResellerClientDialogComponent, {
      width: "600px",
      data: { resellerDomain: reseller.domain },
      panelClass: "app-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
      }
    });
  }

  removeClient(reseller: ResellerWithLicenses, targetDomain: string) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant("reseller.confirmRemove", {
            domain: targetDomain,
          }) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.resellerService
            .removeClient(reseller.domain, targetDomain)
            .subscribe({
              next: () => {
                this.toastr.success(
                  this.translate.instant("reseller.removeSuccess") as string,
                );
                this.loadData();
              },
              error: () => {
                this.toastr.error(
                  this.translate.instant("reseller.removeError") as string,
                );
              },
            });
        }
      });
  }

  attachDomain(reseller: ResellerWithLicenses) {
    const dialogRef = this.dialog.open(AttachDomainDialogComponent, {
      width: "600px",
      data: { resellerDomain: reseller.domain },
      panelClass: "app-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success(
          this.translate.instant("reseller.attachSuccess") as string,
        );
        this.loadData();
      }
    });
  }

  detachDomain(reseller: ResellerWithLicenses, targetDomain: string) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant("reseller.confirmDetach", {
            domain: targetDomain,
          }) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.resellerService
            .detachDomain(reseller.domain, targetDomain)
            .subscribe({
              next: () => {
                this.toastr.success(
                  this.translate.instant("reseller.detachSuccess") as string,
                );
                this.loadData();
              },
              error: (err: { error?: { message?: string } }) => {
                this.toastr.error(
                  err.error?.message ||
                    (this.translate.instant("reseller.detachError") as string),
                );
              },
            });
        }
      });
  }

  isExpanded(domain: string): boolean {
    return this.expandedDomains.has(domain);
  }

  isSubdomain(element: ResellerTableRow): boolean {
    return Boolean(element.parentDomain);
  }

  hasSubdomains(domain: string): boolean {
    const partner = this.partners.get(domain);
    return partner
      ? partner.subdomains && partner.subdomains.length > 0
      : false;
  }

  shouldShowExpandIcon(element: ResellerTableRow): boolean {
    return !this.isSubdomain(element) && this.hasSubdomains(element.domain);
  }

  goToDomain = (domain: string): void => {
    this.store.waitForAction(CHANGE_ACCOUNT_SUCCESS).subscribe(() => {
      void this.router.navigate(["signatures"]);
    });
    this.store.dispatch(new ChangeAccountAction(domain));
  };

  private updateDataSource(): void {
    const result: ResellerTableRow[] = [];

    Array.from(this.partners.values()).forEach((partner) => {
      result.push({
        ...partner,
        parentDomain: null,
        isExpanded: this.expandedDomains.has(partner.domain),
      });

      if (this.expandedDomains.has(partner.domain)) {
        partner.subdomains.forEach((subdomain) => {
          result.push({
            domain: subdomain.domain,
            name: subdomain.domain,
            photoUrl: null,
            totalLicenseCount: subdomain.licenseCount,
            subdomains: [],
            parentDomain: partner.domain,
            isExpanded: false,
          });
        });
      }
    });

    this.dataSource.data = result;
  }
}
